"use client";

// The onboarding shell — a left-leaning content column (brand lockup, two-tone
// heading, then the form) beside the AuthStage: netti on a pane of light with
// memory rising through it (desktop), or planted low behind the form and
// revealed as the page's footer (mobile). No card, no box: the form
// floats on the ground, inputs are soft fields. netti holds one exact look
// here (AuthStage), so the room keeps the steady boot-blue night.
//
// THE ROOM IS DARK NOW (law_auth_breathing_ground, revised 2026-09-09). The
// ground still breathes with `--organism-hue` exactly as before — the engine
// writes the hue per frame and that IS the easing — but it is a deep, saturated
// night rather than a wash over the egg-white, and the ink ladder is inverted
// LOCALLY (components/auth.css, `.auth-night`) so every text layer is white.
// The inversion never touches `:root`: the door can be dark without the rest of
// the app finding out.

import React, { useEffect, useId, useRef, useState } from "react";
import AuthStage from "./auth/AuthStage";
import SiteMenu from "./SiteMenu";
import "./auth.css";

/** The night ground at netti's blue (the door holds that hue now). Kept in one place so the
 *  element's paint (auth.css) and the body's paint (below) can never disagree —
 *  a seam between the two is exactly what the organism composites against. */
export const AUTH_NIGHT_GROUND = "hsl(222 70% 33%)";
/** The same ground frozen at netti's boot hue — for anything that has to paint
 *  BEFORE the engine exists (the boot veil, a route's `:root` declaration). */
export const AUTH_NIGHT_GROUND_STATIC = "hsl(222 70% 33%)";

export default function AuthShell({
  children,
  /** the word after the slash in the lockup: "create" · "sign in" · "reset" … */
  suffix,
  /** the two-tone H3 heading — usually <AuthHeading lead accent /> */
  heading,
  /** a single quiet line (optional; used where there's no heading) */
  whisper,
  /** the deep breathing ground + the locally inverted ladder. On everywhere;
   *  the escape hatch exists so a future surface can ask for the pale room. */
  night = true,
}: {
  children: React.ReactNode;
  suffix?: string;
  heading?: React.ReactNode;
  whisper?: string;
  /** retired — netti holds one exact look at the door now (AuthStage). Kept
   *  so existing callers compile; it no longer does anything. */
  wander?: boolean;
  night?: boolean;
}) {
  // THE BODY CARRIES THE GROUND TOO. The organism engine reads the BODY's
  // rendered background every tick to composite the bloom over it (uBg — see
  // MemoryOrganism.renderedGroundRGB), so a page whose ground lives only on its
  // own element gets a lit rectangle where the creature meets the page. The
  // value is left as CSS rather than resolved here on purpose: it re-resolves
  // from `--organism-hue` on its own, so this stays one write, not a per-frame
  // one. `hsl()` and not `color-mix()` because the engine reads the computed
  // colour with an rgb() matcher.
  useEffect(() => {
    if (!night) return;
    const prev = document.body.style.backgroundColor;
    document.body.style.setProperty("background-color", AUTH_NIGHT_GROUND);
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, [night]);

  return (
    <main className={night ? "auth-main auth-night" : "auth-main"}>
      {/* the ONE navigator — the site crumb + ≡ menu, present on every page.
          Pre-sign-in surface: no signed-in sections, just the innernet tree. */}
      <SiteMenu
        trail={suffix ? [{ label: suffix }] : []}
        current={{ id: "auth", label: suffix ?? "innernet", items: [] }}
        // the door is already open here — the form's own enter is the one CTA
        enter={false}
      />

      <div className="auth-content">
        <div className="auth-body">
          {heading}
          {whisper ? <p className="auth-whisper">{whisper}</p> : null}
          {children}
        </div>
      </div>

      {/* what lies past the door: netti on a pane of light with cells of
          memory rising through it (desktop) · the page's footer, where netti
          rises and sharpens as you scroll to it (mobile). */}
      <AuthStage />
    </main>
  );
}

/** The two-tone onboarding heading: a muted lead line, then an emphasized line
 *  painted with the living accent gradient. Pass an array of phrases for the
 *  accent to fade between marketing taglines. */
export function AuthHeading({
  lead,
  accent,
  className,
}: {
  lead: string;
  accent: string | string[];
  /** for the reveal when a surface turns into another one (`.auth-turn`). */
  className?: string;
}) {
  return (
    <h1 className={className ? `auth-head ${className}` : "auth-head"}>
      <span className="lead">{lead}</span>
      {Array.isArray(accent) ? <RotatingAccent phrases={accent} /> : <span className="accent">{accent}</span>}
    </h1>
  );
}

/** The accent line, fading between taglines to entice. Single phrase or
 *  reduced-motion → static. */
function RotatingAccent({ phrases }: { phrases: string[] }) {
  const [i, setI] = useState(0);
  const [shown, setShown] = useState(true);
  useEffect(() => {
    if (phrases.length < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    let swap: ReturnType<typeof setTimeout>;
    const cycle = setInterval(() => {
      setShown(false); // fade the current line out
      swap = setTimeout(() => {
        setI((p) => (p + 1) % phrases.length);
        setShown(true); // fade the next one in
      }, 600);
    }, 4000);
    return () => {
      clearInterval(cycle);
      clearTimeout(swap);
    };
  }, [phrases.length]);
  return (
    <span className="accent" style={{ opacity: shown ? 1 : 0, transition: "opacity 0.6s var(--ease-soft)" }}>
      {phrases[i]}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * PasswordField — THE password field, everywhere the door asks for one.
 *
 * It is `<Field>`'s markup (`.field` · `.field-label` · `.input-soft`, so there
 * is still exactly ONE field language, law_inputs) with the reveal seated
 * inside it at the right. The control is a real `<button type="button">`: it
 * lives beside the input, never inside a `<label>`, so it can never submit the
 * form and it is reachable by keyboard in the order you'd expect. `aria-pressed`
 * carries the state and the `aria-label` says which way the press goes.
 *
 * NOTHING MOVES WHEN IT TOGGLES. The mark is mounted in both states and the
 * field's right lane is reserved in both, so there is no reflow; and because a
 * browser resets an input's selection when its `type` changes, the caret is
 * captured before the flip and restored after it. Focus is deliberately NOT
 * stolen: pressing the control leaves you on the control.
 * ──────────────────────────────────────────────────────────────────────────── */

type PasswordFieldProps = {
  label: string;
  error?: string | null;
  /** a quiet companion on the label's line, right-aligned ("forgot password?") */
  aside?: React.ReactNode;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "type">;

export function PasswordField({ label, error, aside, id, className, ...input }: PasswordFieldProps) {
  const auto = useId();
  const fieldId = id ?? auto;
  const [shown, setShown] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const caret = useRef<{ start: number | null; end: number | null; focused: boolean } | null>(null);

  // restore the caret across the type change (see the note above)
  useEffect(() => {
    const el = ref.current;
    const was = caret.current;
    caret.current = null;
    if (!el || !was) return;
    try {
      if (was.focused) el.focus({ preventScroll: true });
      if (was.start !== null) el.setSelectionRange(was.start, was.end ?? was.start);
    } catch {
      /* a browser that refuses the selection API on this input — nothing to do */
    }
  }, [shown]);

  const toggle = () => {
    const el = ref.current;
    if (el) {
      caret.current = {
        start: el.selectionStart,
        end: el.selectionEnd,
        focused: typeof document !== "undefined" && document.activeElement === el,
      };
    }
    setShown((v) => !v);
  };

  return (
    <div className="field" data-error={error ? "true" : undefined}>
      {aside ? (
        <span className="auth-pw__labelrow">
          <label className="field-label" htmlFor={fieldId}>
            {label}
          </label>
          {aside}
        </span>
      ) : (
        <label className="field-label" htmlFor={fieldId}>
          {label}
        </label>
      )}
      <div className="auth-pw__well">
        <input
          ref={ref}
          id={fieldId}
          type={shown ? "text" : "password"}
          className={["input-soft", className].filter(Boolean).join(" ")}
          aria-invalid={error ? true : undefined}
          {...input}
        />
        <button
          type="button"
          className="auth-pw__reveal"
          aria-pressed={shown}
          aria-label={shown ? "hide password" : "show password"}
          aria-controls={fieldId}
          onClick={toggle}
        >
          <EyeMark off={shown} />
        </button>
      </div>
      {error ? <span className="field-error">{error}</span> : null}
    </div>
  );
}

/** The reveal's mark — one `currentColor` stroke drawing in the house weight
 *  (1.6 in a 24 box, round caps and joins). The eye is the same in both states;
 *  only the stroke closing it arrives, so the mark reads as one object changing
 *  rather than two icons swapping. */
function EyeMark({ off }: { off: boolean }) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.7 12S6.3 5.9 12 5.9 21.3 12 21.3 12 17.7 18.1 12 18.1 2.7 12 2.7 12Z" />
      <circle cx="12" cy="12" r="2.75" />
      <path className="auth-pw__slash" d="M4.6 19.4 19.4 4.6" style={{ opacity: off ? 1 : 0 }} />
    </svg>
  );
}

// shared ambient form atoms (kept for surfaces not yet on <Field>/<PillButton>).
export const authAtoms = {
  field: { marginBottom: "1.6rem" } as React.CSSProperties,
  label: {
    display: "block",
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    textTransform: "lowercase",
    color: "var(--ink-2)",
    marginBottom: "0.4rem",
  } as React.CSSProperties,
  err: {
    fontSize: "0.85rem",
    color: "var(--ink)",
    margin: "0 0 1.6rem",
    paddingLeft: "0.85rem",
    borderLeft: "2px solid var(--cite-project)",
    lineHeight: 1.55,
  } as React.CSSProperties,
  ok: {
    fontSize: "0.85rem",
    color: "var(--accent-ink)",
    margin: "0 0 1.6rem",
    paddingLeft: "0.85rem",
    borderLeft: "2px solid var(--accent)",
    lineHeight: 1.55,
  } as React.CSSProperties,
  quiet: {
    // the night room reads white on deep colour: the quiet rung is --ink-3,
    // which measures 5.6:1 at the worst hue on the worst ground.
    fontSize: "0.82rem",
    color: "var(--ink-3)",
  } as React.CSSProperties,
};
